import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Slide,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useMediaQuery } from "@mui/material";
import { useParticipantById } from "@/hooks/useParticipants";
import { useParams } from "react-router-dom";
import GoalStepCard from "@/ui/content/clients/goals/GoalStepCard";
import GoalCard from "@/ui/content/clients/goals/GoalCard";
import GoalHeader from "@/ui/content/clients/goals/GoalHeader";
import { useAppTheme } from "@/utils/theme";
import { useTranslation } from "react-i18next";
import { ParticipantResponse } from "@/domain/participant.interface";
import {
  useDeleteAssignedGoal,
  useDeleteAssignedGoalStep,
  useGoalsByEnrollment,
} from "@/hooks/useGoals";
import { GoalTemplateModal } from "@/ui/content/clients/GoalTemplateModal";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import { useUserStore } from "@/hooks/useUserStore";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import SkeletonLoader from "@/components/SkeletonLoader";
import { Goal, GoalStepStatus, Step } from "@/domain/goal.interface";
import ClientStepModal from "@/ui/modals/clients/ClientStepModal";
import { usePageStore } from "@/hooks/usePageStore";
import {
  useGetAlertsByOfficerId,
  useUpdateAlertIsViewed,
} from "@/hooks/useAlerts";
import GenericAlertModal from "@/components/AlertModal";
import DeleteGoalStepConfirmationModal from "@/ui/modals/clients/DeleteGoalStepConfirmationModal";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { ErrorResponse } from "@/domain/error.interface";

export const goalBoxStyle: SxProps<Theme> = (theme) => ({
  padding: 3,
  boxShadow: 1,
  borderRadius: 2,
  bgcolor: theme.palette.surface.light,
});

interface GoalStepTitleProps {
  children: React.ReactNode;
}

type StandardDeleteAlertState = {
  open: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
};

type TypedDeleteTarget =
  | { type: "goal"; goal: Goal }
  | { type: "step"; goal: Goal; step: Step }
  | null;

const GoalStepTitle: React.FC<GoalStepTitleProps> = ({ children }) => {
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={{ paddingBottom: 2.5, minHeight: 60 }}
    >
      {children}
    </Box>
  );
};

export { GoalStepTitle };

const GoalsPage: React.FC = () => {
  const theme = useAppTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { participantId } = useParams();
  const { setPageTitle } = usePageStore();
  const { t } = useTranslation(["clients", "common"]);
  const { showSnackBar } = useFeedBack();

  const createAuditLog = useCreateAuditlog();
  const { userInfo, userHasAccessToCode } = useUserStore();
  const { data: participantResponse, isLoadingParticipant } =
    useParticipantById(participantId || "");

  const {
    data: goalsData,
    isLoadingGoals,
    refetchGoals,
  } = useGoalsByEnrollment(participantResponse?.lastEnrollment?._id || "");

  const { alerts } = useGetAlertsByOfficerId(userInfo?._id || "", 100, 1);

  const [selectedGoal, setSelectedGoal] = useState<string | null>(null);
  const [slideDirection, setSlideDirection] = useState<
    "left" | "right" | "up" | "down"
  >("left");
  const [isAddGoalModalOpen, setIsAddGoalModalOpen] = useState(false);
  const [showStepModal, setShowStepModal] = useState(false);
  const [selectedStep, setSelectedStep] = useState<Step | null>(null);
  const [standardDeleteAlert, setStandardDeleteAlert] =
    useState<StandardDeleteAlertState>({
      open: false,
      title: "",
      message: "",
      onConfirm: () => {},
    });
  const [typedDeleteTarget, setTypedDeleteTarget] =
    useState<TypedDeleteTarget>(null);
  const { mutateAsync: markAsViewed } = useUpdateAlertIsViewed();
  const deleteAssignedGoal = useDeleteAssignedGoal();
  const deleteAssignedGoalStep = useDeleteAssignedGoalStep();

  const enrollmentId = participantResponse?.lastEnrollment?._id || "";
  const accountId = `${process.env.REACT_APP_ACCOUNT_ID || ""}`;
  const participantFullName =
    `${participantResponse?.participant?.firstName || ""} ${
      participantResponse?.participant?.lastName || ""
    }`.trim();

  const handleSelectGoal = (goalId: string) => {
    setSlideDirection("right");
    setSelectedGoal(goalId);
  };

  const handleGoBack = () => {
    setSlideDirection("right");
    setSelectedGoal(null);
  };

  const handleOnSelectGoalStep = (stepData: Step) => {
    const matchAlert = alerts?.find(
      (alert) =>
        alert.participantId === participantResponse?.participant?._id &&
        alert.code === "PARTICIPANT_STEP_COMPLETION" &&
        alert.referenceId === stepData._id
    );

    if (matchAlert && !matchAlert.isViewed) {
      markAsViewed({
        alertId: matchAlert._id,
      });
    }
    if (stepData.status === GoalStepStatus.PENDING_SUBMIT) {
      return;
    }

    setSelectedStep(stepData);
    setShowStepModal(true);
  };

  const handleOpenAddGoalModal = () => {
    setIsAddGoalModalOpen(true);
  };
  const handleCloseAddGoalModal = () => {
    setIsAddGoalModalOpen(false);
  };

  const handleOnGoalAdded = async () => {
    handleCloseAddGoalModal();
    await refetchGoals();
  };

  const showPermissionAlert = () => {
    setStandardDeleteAlert({
      open: true,
      title: "Insufficient permissions",
      message: t("client_profile.insufficient_permissions_execute"),
      onConfirm: () =>
        setStandardDeleteAlert((prevState) => ({
          ...prevState,
          open: false,
        })),
    });
  };

  const getErrorMessage = (error: unknown) => {
    const err = error as ErrorResponse;
    return (
      (Array.isArray(err.message) ? err.message[0] : err.message) ||
      err.error ||
      "An error occurred"
    );
  };

  const createDeleteGoalAuditLog = (goal: Goal) => {
    createAuditLog.mutate({
      appType: "WEB_BACK_OFFICE",
      module: MODULE_NAME.PARTICIPANT_GOALS,
      option: OPTIONS.DELETE_GOAL_FROM_PARTICIPANT,
      actionCode: ACTION_CODE.WEB_PART_GOAL_DELETE,
      action: "When deleted goal",
      detail: `Deleted goal ${goal.name} of ${participantFullName}`,
      transactionDate: new Date(),
      accountId,
      createdBy: userInfo?._id as string,
      targetId: participantResponse?.participant?._id as string,
      programId: participantResponse?.lastEnrollment.programId,
      orgUnitId: participantResponse?.lastEnrollment.orgUnitId,
    });
  };

  const createDeleteStepAuditLog = (goal: Goal, step: Step) => {
    createAuditLog.mutate({
      appType: "WEB_BACK_OFFICE",
      module: MODULE_NAME.PARTICIPANT_GOALS,
      option: OPTIONS.DELETE_STEP_FROM_PARTICIPANT,
      actionCode: ACTION_CODE.WEB_PART_GOAL_STEP_DELETE,
      action: "When deleted step",
      detail: `Deleted step ${step.name} of ${participantFullName}`,
      transactionDate: new Date(),
      accountId,
      createdBy: userInfo?._id as string,
      targetId: participantResponse?.participant?._id as string,
      programId: participantResponse?.lastEnrollment.programId,
      orgUnitId: participantResponse?.lastEnrollment.orgUnitId,
    });
  };

  const handleDeleteGoal = async (goal: Goal) => {
    if (!enrollmentId || !goal._id || !userInfo?._id) return;

    setStandardDeleteAlert((prevState) => ({ ...prevState, open: false }));
    setTypedDeleteTarget(null);

    try {
      await deleteAssignedGoal.mutateAsync({
        enrollmentId,
        goalId: goal._id,
        updatedBy: userInfo._id,
      });
      showSnackBar("The goal has been deleted.", "success");
      createDeleteGoalAuditLog(goal);
      if (selectedGoal === goal._id) {
        setSelectedGoal(null);
      }
      await refetchGoals();
    } catch (error) {
      showSnackBar(getErrorMessage(error), "error");
    }
  };

  const handleDeleteStep = async (goal: Goal, step: Step) => {
    if (!enrollmentId || !goal._id || !step._id || !userInfo?._id) return;

    setStandardDeleteAlert((prevState) => ({ ...prevState, open: false }));
    setTypedDeleteTarget(null);

    try {
      await deleteAssignedGoalStep.mutateAsync({
        enrollmentId,
        goalId: goal._id,
        stepId: step._id,
        updatedBy: userInfo._id,
      });
      showSnackBar("The step has been deleted.", "success");
      createDeleteStepAuditLog(goal, step);
      await refetchGoals();
    } catch (error) {
      showSnackBar(getErrorMessage(error), "error");
    }
  };

  const handleOpenDeleteGoal = (goal: Goal) => {
    if (!userHasAccessToCode("PARTICIPANT_GOAL_DELETE")) {
      showPermissionAlert();
      return;
    }

    const hasSteps = goal.steps.some((step) => !step.isDeleted);
    if (hasSteps) {
      setTypedDeleteTarget({ type: "goal", goal });
      return;
    }

    setStandardDeleteAlert({
      open: true,
      title: "Delete",
      message: "Are you sure you want to delete this goal?",
      onConfirm: () => handleDeleteGoal(goal),
    });
  };

  const handleOpenDeleteStep = (goal: Goal, step: Step) => {
    if (!userHasAccessToCode("PARTICIPANT_STEP_DELETE")) {
      showPermissionAlert();
      return;
    }

    const hasAssociatedPoints =
      step.status === GoalStepStatus.PENDING_APPROVAL ||
      step.status === GoalStepStatus.APPROVED;
    if (hasAssociatedPoints) {
      setTypedDeleteTarget({ type: "step", goal, step });
      return;
    }

    setStandardDeleteAlert({
      open: true,
      title: "Delete",
      message: "Are you sure you want to delete this step?",
      onConfirm: () => handleDeleteStep(goal, step),
    });
  };

  const auditLogViewGoalsCalledRef = React.useRef(false);

  React.useEffect(() => {
    if (
      !isLoadingParticipant &&
      userInfo &&
      !auditLogViewGoalsCalledRef.current
    ) {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.PARTICIPANT_GOALS,
        option: OPTIONS.PARTICIPANT_GOALS_LIST,
        actionCode: ACTION_CODE.WEB_PART_GOAL_LIST_VIEW,
        action: "When entering to view the list",
        detail: `Viewed the list of goals of ${participantFullName}`,
        transactionDate: new Date(),
        accountId,
        createdBy: userInfo?._id as string,
        targetId: participantResponse?.participant?._id as string,
        programId: participantResponse?.lastEnrollment.programId,
        orgUnitId: participantResponse?.lastEnrollment.orgUnitId,
      });
      auditLogViewGoalsCalledRef.current = true;
    }
  }, [isLoadingParticipant, userInfo]);

  const selectedGoalData =
    goalsData && goalsData?.find((goal) => goal._id === selectedGoal);
  const visibleGoalsData = goalsData?.filter((goal) => !goal.isDeleted) || [];
  const visibleSelectedGoalSteps =
    selectedGoalData?.steps.filter((step) => !step.isDeleted) || [];
  const isDeleteLoading =
    deleteAssignedGoal.isPending || deleteAssignedGoalStep.isPending;

  useEffect(() => {
    setPageTitle(t("goals.page_title"));
  }, []);

  return (
    <div className="w-full overflow-y-auto scroll-smooth">
      <div className="full-width-container mx-auto">
        <div className="flex flex-col items-stretch justify-start min-h-screen">
          <GoalHeader
            isMobile={isMobile}
            selectedGoal={!!selectedGoal}
            onGoBack={handleGoBack}
            headerText={t("goals.goals_available")}
            participantResponse={participantResponse as ParticipantResponse}
            isLoadingParticipant={isLoadingParticipant}
          />

          <Grid container mt={0} spacing={2}>
            <Slide
              direction={slideDirection}
              mountOnEnter
              unmountOnExit
              in={!selectedGoal || !isMobile}
              timeout={250}
            >
              {!isMobile || !selectedGoal ? (
                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{ display: "flex", flexDirection: "column" }}
                >
                  <Box sx={goalBoxStyle(theme)}>
                    <GoalStepTitle>
                      <Typography
                        color={theme.palette.textMain.dark2}
                        variant="headlineSmall"
                      >
                        {t("goals.goals_available")}
                      </Typography>
                      <Button
                        startIcon={<AddIcon />}
                        variant="contained"
                        sx={{
                          borderRadius: 2,
                          fontWeight: 500,
                        }}
                        onClick={handleOpenAddGoalModal}
                      >
                        {t("common:add").toUpperCase()}
                      </Button>
                    </GoalStepTitle>
                    <Grid container>
                      <SkeletonLoader
                        skeletonType="rectangular"
                        isLoading={isLoadingGoals || isLoadingParticipant}
                        skeletonHeight={90}
                        skeletonWidth={400}
                        skeletonCount={3}
                      >
                        {visibleGoalsData.length > 0 ? (
                          visibleGoalsData.map((goal) => (
                            <Grid item xs={12} key={goal._id}>
                              <GoalCard
                                goalData={goal}
                                isSelected={goal._id === selectedGoal}
                                onDeleteGoal={handleOpenDeleteGoal}
                                onSelectGoal={() =>
                                  handleSelectGoal(goal._id || "")
                                }
                              />
                            </Grid>
                          ))
                        ) : (
                          <Typography variant="body1" color="text.secondary">
                            {t("goals.no_goals_added_yet")}
                          </Typography>
                        )}
                      </SkeletonLoader>
                    </Grid>
                  </Box>
                </Grid>
              ) : (
                <Box></Box>
              )}
            </Slide>

            <Slide
              direction="left"
              mountOnEnter
              unmountOnExit
              in={!!selectedGoal}
              timeout={250}
              key={selectedGoal}
            >
              <Grid
                item
                xs={12}
                md={6}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <Box sx={goalBoxStyle(theme)}>
                  <GoalStepTitle>
                    <Typography
                      color={theme.palette.textMain.dark2}
                      variant="headlineSmall"
                    >
                      {t("goals.steps_for_goal", {
                        goal: selectedGoalData?.name,
                      })}
                    </Typography>
                  </GoalStepTitle>
                  <Grid container>
                    <SkeletonLoader
                      skeletonType="rectangular"
                      isLoading={isLoadingGoals || isLoadingParticipant}
                      skeletonHeight={90}
                      skeletonWidth={400}
                      skeletonCount={3}
                    >
                      {visibleSelectedGoalSteps.map((step, index) => (
                        <Grid
                          item
                          xs={12}
                          key={`${selectedGoalData?._id}-${step._id}-${index}`}
                        >
                          <GoalStepCard
                            onSelectGoalStep={handleOnSelectGoalStep}
                            onDeleteGoalStep={(stepData) =>
                              selectedGoalData &&
                              handleOpenDeleteStep(selectedGoalData, stepData)
                            }
                            stepData={step}
                          />
                        </Grid>
                      ))}
                    </SkeletonLoader>
                  </Grid>
                </Box>
              </Grid>
            </Slide>
          </Grid>

          {isAddGoalModalOpen ? (
            <GoalTemplateModal
              open={isAddGoalModalOpen}
              onCancel={handleCloseAddGoalModal}
              onAdd={handleOnGoalAdded}
              enrollmentId={participantResponse?.lastEnrollment?._id || ""}
            />
          ) : null}

          {showStepModal ? (
            <ClientStepModal
              open={showStepModal}
              onClose={() => setShowStepModal(false)}
              participant={participantResponse as ParticipantResponse}
              goalId={selectedGoal as string}
              stepData={selectedStep as Step}
              refetchGoals={refetchGoals}
            />
          ) : null}

          {standardDeleteAlert.open ? (
            <GenericAlertModal
              onCancel={() =>
                setStandardDeleteAlert((prevState) => ({
                  ...prevState,
                  open: false,
                }))
              }
              onClick={standardDeleteAlert.onConfirm}
              title={standardDeleteAlert.title}
              description={standardDeleteAlert.message}
              cancelText="Cancel"
              submitText="Accept"
              isLoading={isDeleteLoading}
              theme={theme}
            />
          ) : null}

          {typedDeleteTarget ? (
            <DeleteGoalStepConfirmationModal
              open={!!typedDeleteTarget}
              theme={theme}
              title={
                typedDeleteTarget.type === "goal"
                  ? "Delete goal"
                  : "Delete step"
              }
              description={
                typedDeleteTarget.type === "goal" ? (
                  <>
                    Are you sure you want to delete this goal? This goal
                    contains one or more steps in it. To confirm deletion type{" "}
                    <strong>permanently delete</strong> in the input field.
                  </>
                ) : (
                  <>
                    Are you sure you want to delete this step? This step has the
                    status {typedDeleteTarget.step.status}. To confirm deletion
                    type <strong>permanently delete</strong> in the input field.
                  </>
                )
              }
              isLoading={isDeleteLoading}
              onCancel={() => setTypedDeleteTarget(null)}
              onConfirm={() => {
                if (typedDeleteTarget.type === "goal") {
                  handleDeleteGoal(typedDeleteTarget.goal);
                  return;
                }

                handleDeleteStep(
                  typedDeleteTarget.goal,
                  typedDeleteTarget.step
                );
              }}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default GoalsPage;
